import React, { useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

const GridCanvas = ({ width, height }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    timeRef.current += 0.008;
    const t = timeRef.current;

    ctx.clearRect(0, 0, w, h);

    // Grid lines pulsing
    const gridSize = 40;
    ctx.strokeStyle = 'rgba(0, 240, 255, 0.06)';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < w; x += gridSize) {
      const offset = Math.sin(t + x * 0.01) * 2;
      ctx.beginPath();
      ctx.moveTo(x + offset, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y < h; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y + Math.cos(t + y * 0.01) * 2);
      ctx.stroke();
    }

    // Data nodes
    for (let i = 0; i < 12; i++) {
      const nx = (Math.sin(t * 0.3 + i * 1.8) * 0.4 + 0.5) * w;
      const ny = (Math.cos(t * 0.2 + i * 2.1) * 0.4 + 0.5) * h;
      const radius = 2 + Math.sin(t * 2 + i) * 1;

      ctx.beginPath();
      ctx.arc(nx, ny, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0, 240, 255, ${0.3 + Math.sin(t + i) * 0.2})`;
      ctx.fill();

      // Connection lines
      for (let j = i + 1; j < 12; j++) {
        const nx2 = (Math.sin(t * 0.3 + j * 1.8) * 0.4 + 0.5) * w;
        const ny2 = (Math.cos(t * 0.2 + j * 2.1) * 0.4 + 0.5) * h;
        const dist = Math.hypot(nx2 - nx, ny2 - ny);
        if (dist < 150) {
          ctx.beginPath();
          ctx.moveTo(nx, ny);
          ctx.lineTo(nx2, ny2);
          ctx.strokeStyle = `rgba(0, 240, 255, ${0.08 * (1 - dist / 150)})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = width || 600;
    canvas.height = height || 600;
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [width, height, draw]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
};

const OrganicCanvas = ({ width, height }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    timeRef.current += 0.006;
    const t = timeRef.current;

    ctx.clearRect(0, 0, w, h);

    // Organic flowing curves
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(0, h * 0.3 + i * 50);
      for (let x = 0; x <= w; x += 5) {
        const y = h * 0.3 + i * 50
          + Math.sin(x * 0.008 + t + i * 0.8) * 40
          + Math.sin(x * 0.015 + t * 1.3 + i) * 20
          + Math.sin(x * 0.003 + t * 0.5) * 30;
        ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(224, 90, 71, ${0.08 + i * 0.03})`;
      ctx.lineWidth = 1.5 - i * 0.15;
      ctx.stroke();
    }

    // Organic dots / particles
    for (let i = 0; i < 20; i++) {
      const px = (Math.sin(t * 0.5 + i * 3.7) * 0.35 + 0.5) * w;
      const py = (Math.cos(t * 0.4 + i * 2.3) * 0.35 + 0.5) * h;
      const size = 1.5 + Math.sin(t * 1.5 + i * 2) * 1;

      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(212, 163, 115, ${0.2 + Math.sin(t + i) * 0.15})`;
      ctx.fill();
    }

    // Ink splatter effect
    for (let i = 0; i < 5; i++) {
      const cx = (0.2 + i * 0.15) * w;
      const cy = h * 0.5 + Math.sin(t * 0.3 + i * 1.5) * 80;
      const r = 15 + Math.sin(t + i * 2) * 8;

      ctx.beginPath();
      for (let a = 0; a < Math.PI * 2; a += 0.3) {
        const wobble = r + Math.sin(a * 3 + t + i) * (r * 0.4);
        const x = cx + Math.cos(a) * wobble;
        const y = cy + Math.sin(a) * wobble;
        if (a === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = `rgba(224, 90, 71, ${0.03 + Math.sin(t + i) * 0.01})`;
      ctx.fill();
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = width || 600;
    canvas.height = height || 600;
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [width, height, draw]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
};

export const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Split Background */}
      <div className="absolute inset-0 flex">
        {/* Human side */}
        <div className="w-1/2 bg-human-bg relative">
          <OrganicCanvas width={960} height={1080} />
          {/* Noise overlay */}
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }} />
        </div>
        {/* AI side */}
        <div className="w-1/2 bg-ai-bg relative">
          <GridCanvas width={960} height={1080} />
          {/* Scanlines */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,240,255,0.1) 2px, rgba(0,240,255,0.1) 4px)',
          }} />
        </div>
      </div>

      {/* Collision line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px z-10">
        <motion.div
          className="w-full h-full"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(224,90,71,0.6) 30%, rgba(0,240,255,0.6) 70%, transparent 100%)',
          }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center min-h-[70vh]">
          {/* Human Side Content */}
          <motion.div
            className="lg:pr-16"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <motion.p
              className="text-human-secondary text-sm italic mb-6 tracking-wide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {'// the human side'}
            </motion.p>
            <h1 className="mb-6">
              <span
                className="block text-3xl md:text-5xl lg:text-6xl font-light italic text-human-text leading-tight"
                style={{ letterSpacing: '-0.02em' }}
              >
                Analog
              </span>
              <span
                className="block text-3xl md:text-5xl lg:text-6xl font-light italic text-human-primary leading-tight"
              >
                Wisdom.
              </span>
            </h1>
            <motion.div
              className="w-20 h-px bg-human-primary/50 mb-6"
              initial={{ width: 0 }}
              animate={{ width: 80 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            />
            <p className="text-human-text/90 text-base italic leading-relaxed max-w-md font-light">
              Enterprise-grade System Architecture, Backend, and DevOps. Architected by a Human Expert.
            </p>
          </motion.div>

          {/* AI Side Content */}
          <motion.div
            className="text-right lg:pl-16"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <motion.p
              className="text-ai-text/60 text-sm uppercase tracking-[0.3em] mb-6 font-semibold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              {'// THE MACHINE SIDE'}
            </motion.p>
            <h1 className="mb-6">
              <span
                className="block text-3xl md:text-5xl lg:text-6xl font-bold uppercase text-ai-text leading-tight tracking-widest"
              >
                DIGITAL
              </span>
              <span
                className="block text-3xl md:text-5xl lg:text-6xl font-bold uppercase text-ai-primary leading-tight tracking-widest"
              >
                VELOCITY.
              </span>
            </h1>
            <motion.div
              className="w-20 h-px bg-ai-primary/50 mb-6 ml-auto"
              initial={{ width: 0 }}
              animate={{ width: 80 }}
              transition={{ delay: 1, duration: 0.6 }}
            />
            <p className="text-ai-text/90 text-base uppercase tracking-wider leading-relaxed max-w-md font-medium ml-auto">
              Accelerated by Artificial Intelligence.
            </p>
          </motion.div>
        </div>

        {/* CTA - Center */}
        <motion.div
          className="flex justify-center mt-12 lg:mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <button
            data-testid="hero-cta"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="group relative px-8 py-3 overflow-hidden"
          >
            <span className="absolute inset-0 border border-human-primary/50 group-hover:border-ai-primary/50 transition-colors duration-500" />
            <span className="absolute inset-0 bg-gradient-to-r from-human-primary/10 to-ai-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="relative text-sm uppercase tracking-[0.3em] font-semibold">
              <span className="text-human-primary group-hover:text-human-text transition-colors duration-300">Deploy</span>
              {' '}
              <span className="text-ai-primary group-hover:text-ai-text transition-colors duration-300">the Hybrid Model</span>
            </span>
          </button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <span className="text-ai-text/50 text-xs uppercase tracking-[0.3em]">scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-muted/50 to-transparent" />
        </motion.div>
      </div>
    </section>
  );
};
